import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/ui/use-toast';
import { usersService } from '../services/users';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Navigate } from 'react-router-dom';

function InviteUserForm() {
  const [email, setEmail] = useState('');
  const [inviteeName, setInviteeName] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resent, setResent] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const clearStatus = () => {
    if (formError) setFormError(null);
    if (success) setSuccess(false);
    if (resent) setResent(false);
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !inviteeName) return;

    setFormError(null);
    setIsLoading(true);

    try {
      let captchaToken = '';
      if (executeRecaptcha) {
        captchaToken = await executeRecaptcha('invite_user');
      }

      const data = await usersService.inviteUser({
        email,
        inviteeName,
        message: message || undefined,
        captchaToken,
      });

      if (data.resent) {
        setResent(true);
      } else {
        setSuccess(true);
      }
      setEmail('');
      setInviteeName('');
      setMessage('');

      toast({
        title: data.resent ? t('invitation.resentTitle') : t('invitation.sent'),
        description: data.message,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('auth.unexpectedError');
      setFormError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [email, inviteeName, message, executeRecaptcha, t, toast]);

  return (
    <form onSubmit={handleSubmit}>
      <CardContent className="space-y-4">
        {formError && (
          <div className="p-3 rounded-md bg-destructive/15 text-destructive text-sm">
            {formError}
          </div>
        )}
        {success && (
          <div className="p-3 rounded-md bg-green-500/15 text-green-600 dark:text-green-400 text-sm">
            {t('invitation.sentSuccess')}
          </div>
        )}
        {resent && (
          <div className="p-3 rounded-md bg-blue-500/15 text-blue-600 dark:text-blue-400 text-sm">
            {t('invitation.resentSuccess')}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="inviteeName">{t('invitation.inviteeName')}</Label>
          <Input
            id="inviteeName"
            type="text"
            placeholder={t('invitation.inviteeNamePlaceholder')}
            value={inviteeName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setInviteeName(e.target.value);
              clearStatus();
            }}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">{t('auth.email')}</Label>
          <Input
            id="email"
            type="email"
            placeholder="friend@example.com"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEmail(e.target.value);
              clearStatus();
            }}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="message">
            {t('invitation.message')}
            <span className="text-muted-foreground ml-1 text-xs">({t('invitation.optional')})</span>
          </Label>
          <Textarea
            id="message"
            placeholder={t('invitation.messagePlaceholder')}
            value={message}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setMessage(e.target.value);
              clearStatus();
            }}
            rows={3}
          />
        </div>
      </CardContent>
      <CardFooter>
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? t('invitation.sending') : t('invitation.sendInvitation')}
        </Button>
      </CardFooter>
    </form>
  );
}

export default function InviteUserPage() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string;

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={siteKey}>
      <div className="flex justify-center items-center my-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('invitation.inviteUser')}</CardTitle>
            <CardDescription>{t('invitation.inviteDescription')}</CardDescription>
          </CardHeader>
          <InviteUserForm />
        </Card>
      </div>
    </GoogleReCaptchaProvider>
  );
}
